import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/lib/content";

const ContentContext = createContext(null);

// Deep-merge `incoming` into `base`. Arrays are REPLACED (not merged) so admin
// edits to lists behave intuitively. Primitive empty strings DO override defaults
// (so an admin can blank a heading).
export function deepMerge(base, incoming) {
  if (incoming == null) return base;
  if (Array.isArray(incoming) || Array.isArray(base)) return incoming ?? base;
  if (typeof base !== "object" || typeof incoming !== "object") return incoming;
  const out = { ...base };
  for (const k of Object.keys(incoming)) {
    out[k] = deepMerge(base?.[k], incoming[k]);
  }
  return out;
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      if (data && typeof data === "object") {
        setContent(deepMerge(DEFAULT_CONTENT, data));
      }
    } catch {
      // Keep defaults on error — site still renders.
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Allow admin save handler to update local state without re-fetch.
  const replace = useCallback((next) => {
    setContent(deepMerge(DEFAULT_CONTENT, next));
  }, []);

  return (
    <ContentContext.Provider value={{ content, refresh, replace }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within ContentProvider");
  return ctx;
}

// Convenience derived helper: returns { name, formerName, cin, phone, phoneRaw,
// email, address } as the old COMPANY object.
export function useCompany() {
  const { content } = useContent();
  const c = content.company || {};
  return {
    name: c.name,
    formerName: c.former_name,
    cin: c.cin,
    phone: c.phone,
    phoneRaw: c.phone_raw,
    email: c.email,
    addressLines: [c.address_line1, c.address_line2, c.address_line3].filter(Boolean),
    businessHours: {
      weekdayLabel: c.business_hours_label_weekday,
      weekdayValue: c.business_hours_value_weekday,
      sundayLabel: c.business_hours_label_sunday,
      sundayValue: c.business_hours_value_sunday,
    },
  };
}
